import { useCallback } from 'react';

import {
	EditSquareFilledIcon,
	EditSquareIcon,
	GovBuildingFilledIcon,
	GovBuildingIcon,
	HomeFilledIcon,
	HomeIcon,
	SpeakerFilledIcon,
	SpeakerIcon,
} from '@change/ds-icons';

import {
	ContentSection,
	MainContent,
	SideNavigation,
	SideNavigationItem,
	SideNavigationLayout,
} from '@change/ds-layout/next';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import {
	DM_TAB_CLICK,
	HOME_TAB_CLICK,
	SHARE_TAB_CLICK,
	SUPPORTERS_TAB_CLICK,
} from 'src/app/pages/starterDashboardV2/shared/constants';
import { ButtonVariantProvider } from 'src/app/pages/starterDashboardV2/shared/contexts';

import { DesktopTabs } from './components/DashboardTabs/components/DesktopTabs';
import { DecisionMakersList } from './components/DecisionMakersList';
import { RecentSupporters } from './components/EngageSupporters/components/RecentSupporters';
import { GrowPetition } from './components/GrowPetition';
import { NotificationsCenter } from './components/NotificationsCenter';
import { PetitionInsights } from './components/PetitionInsights';
import { PetitionOverview } from './components/PetitionOverview';
import { Recommendations } from './components/Recommendations';
import { StepsToVictory } from './components/StepsToVictory';
import { UpdateSupporters } from './components/UpdateSupporters';

const TAB_CLICK_EVENTS: Record<string, string> = {
	home: HOME_TAB_CLICK,
	share: SHARE_TAB_CLICK,
	dms: DM_TAB_CLICK,
	supporters: SUPPORTERS_TAB_CLICK,
};

const DesktopNavigation = () => {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const track = useTracking();
	const switchTab = useCallback(
		(currentTab: string, previousTab: string) => {
			const event = TAB_CLICK_EVENTS[currentTab];
			void track(event, {
				context: previousTab,
			});
		},
		[track],
	);

	return (
		<SideNavigationLayout className="hidden lg:block" onTabChange={switchTab}>
			<SideNavigation>
				<SideNavigationItem selectedIcon={<HomeFilledIcon />} icon={<HomeIcon />} id="home" label="Home" />
				<SideNavigationItem
					selectedIcon={<SpeakerFilledIcon />}
					icon={<SpeakerIcon />}
					id="share"
					label="Share your petition"
				/>
				<SideNavigationItem
					selectedIcon={<GovBuildingFilledIcon />}
					icon={<GovBuildingIcon />}
					id="dms"
					label="Connect to decision makers"
				/>
				<SideNavigationItem
					selectedIcon={<EditSquareFilledIcon />}
					icon={<EditSquareIcon />}
					id="supporters"
					label="Message your supporters"
				/>
			</SideNavigation>
			<MainContent>
				<ContentSection id="home" className="flex flex-col gap-8 pr-4">
					<NotificationsCenter />
					<PetitionOverview />
					<Recommendations slug={slug} />
					<RecentSupporters />
					<PetitionInsights />
				</ContentSection>

				<ContentSection id="share" className="flex flex-col gap-8 pr-4">
					<Recommendations slug={slug} />
					<GrowPetition />
					<StepsToVictory />
				</ContentSection>

				<ContentSection id="dms" className="flex flex-col gap-8 pr-4">
					<DecisionMakersList />
				</ContentSection>

				<ContentSection id="supporters" className="flex flex-col gap-8 pr-4">
					<UpdateSupporters />
				</ContentSection>
			</MainContent>
		</SideNavigationLayout>
	);
};

const MobileNavigation = () => {
	return (
		<main className="col-span-4 lg:hidden">
			{/* TODO: Remove this provider once the tabs component has been migrated to sunrise */}
			{/* Naming is deceiving, even though the component's name is **Desktop** Tabs, we need to use it to render tabs in mobile */}
			<DesktopTabs />
		</main>
	);
};

export function PageBody() {
	return (
		<ButtonVariantProvider data-qa="button-variant-context">
			<DesktopNavigation />
			<MobileNavigation />
		</ButtonVariantProvider>
	);
}
